<template>
  <div class="login-page d-flex align-items-center justify-content-center">
    <!-- Círculos decorativos para el efecto de fondo -->
    <div class="decor-circle circle-1"></div>
    <div class="decor-circle circle-2"></div>

    <div class="login-card shadow-lg p-4 p-md-5">
      <div class="text-center mb-4">
        <p class="text-dark fw-bold">Maki-OT</p>
        <img src="../assets/logo.png" alt="Logo OT" class="mb-3 corporate-logo" />
        <p class="text-primary fw-medium">Sistema de Gestión de Operaciones</p>
      </div>

      <div class="form-floating mb-3">
        <input v-model="usuario" type="text" class="form-control custom-input" id="userInput" placeholder="Usuario"
          @keyup.enter="focusPassword" />
        <label for="userInput" class="text-muted"><i class="bi bi-person me-2"></i>Usuario</label>
      </div>

      <div class="form-floating mb-4">
        <input ref="passwordInput" v-model="contrasena" type="password" class="form-control custom-input" id="passInput"
          placeholder="Contraseña" @keyup.enter="login" />
        <label for="passInput" class="text-muted"><i class="bi bi-lock me-2"></i>Contraseña</label>
      </div>

      <button class="btn btn-login w-100 py-3 fw-bold text-uppercase tracking-wider shadow-sm" :disabled="loading"
        @click="login">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
        {{ loading ? 'Iniciando...' : 'Entrar al Sistema' }}
      </button>

      <div class="text-center mt-3" v-if="error">
        <div class="alert alert-danger py-2 px-3 small border-0 shadow-sm rounded-3">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ error }}
        </div>
      </div>

      <div class="mt-5 text-center">
        <p class="text-muted-light x-small mb-0">CyM Contulmo • Versión {{ version }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import http from '../api/http'
import { useSessionStore } from '../stores/session'
import { useRouter } from 'vue-router'
import packageJson from '../../package.json'

const version = packageJson.version

const usuario = ref('')
const contrasena = ref('')
const error = ref('')
const loading = ref(false)
const passwordInput = ref(null)

const session = useSessionStore()
const router = useRouter()

function focusPassword() {
  passwordInput.value?.focus()
}

async function login() {
  if (!usuario.value || !contrasena.value) {
    error.value = 'Complete todos los campos'
    return
  }

  loading.value = true
  error.value = ''

  try {
    const res = await http.post('/api/auth/login', {
      usuario: usuario.value,
      contrasena: contrasena.value
    })

    if (res.data.success) {
      // Sincronizar sesión en Pinia y navegar sin full reload
      session.setUser(res.data.user)
      router.push('/')
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Credenciales inválidas'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-page {
  position: relative;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  overflow: hidden;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.decor-circle {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 0;
  opacity: 0.4;
}

.circle-1 {
  width: 400px;
  height: 400px;
  background: #3b82f6;
  top: -100px;
  right: -100px;
}

.circle-2 {
  width: 300px;
  height: 300px;
  background: #0ea5e9;
  bottom: -50px;
  left: -50px;
}

.login-card {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 440px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 28px;
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.corporate-logo {
  height: 80px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.08));
}

.fw-black {
  font-weight: 900;
}

.tracking-tight {
  letter-spacing: -0.04em;
}

.tracking-wider {
  letter-spacing: 0.1em;
}

.x-small {
  font-size: 0.7rem;
}

.text-muted-light {
  color: #94a3b8;
}

.custom-input {
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 0.75rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.custom-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15);
  background-color: #fff;
}

.btn-login {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border: none;
  color: white;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(37, 99, 235, 0.3);
  filter: brightness(1.1);
}

.btn-login:active {
  transform: translateY(0);
}

.alert {
  animation: shake 0.4s ease-in-out;
}

@keyframes shake {

  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  75% {
    transform: translateX(5px);
  }
}

/* Responsive tweaks */
@media (max-width: 576px) {
  .login-card {
    margin: 1.5rem;
    padding: 2.5rem 1.5rem !important;
    border-radius: 24px;
  }
}
</style>
